import styled, { css } from "styled-components";
import {
  DETAIL_TOTALS_FOOTER_BG,
  DETAIL_TOTALS_FOOTER_BORDER,
  DETAIL_TOTALS_FOOTER_HEIGHT,
  DETAIL_TOTALS_FOOTER_SHADOW,
  detailTotalsFooterRowCss,
  detailTotalsFooterValueCss,
} from "./detailTotalsFooter.styles";
import { tableHeaderTextCss, tableNumericCss } from "./tableTypography.styles";

export const StyledTableWrapper = styled.div`
  flex: 1 1 auto;
  font-size: 14px;
  margin: 12px 24px 0;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &.bulk {
    // Flex column layout required for virtualization to fill available height
    display: flex;
    flex-direction: column;
    height: calc(100vh - 228.5px);
    margin: auto 0;
  }

  &.detail {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 280px);
  }

  &.detail.purchaseFramed {
    box-sizing: border-box;
    margin: 0 24px 16px;
    width: calc(100% - 48px);
    border: 1px solid #d3dbe8;
    border-radius: 0 6px 6px 6px;
    overflow: auto;
  }
`;

export const StyledTable = styled.table`
  border-collapse: collapse;
  width: max-content;
  table-layout: fixed;

  &.bulk {
    // Ensure table takes full width/height in bulk mode for correct virtualizer sizing
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &.detail {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

export const StyledTHead = styled.thead`
  background-color: var(--surface-100, #ffffff);
  position: sticky;
  top: 0;
  z-index: 2;

  &.bulk {
    background-color: var(--Mist, #f7f8f9);
  }

  &.detail.purchaseFramed {
    background-color: #eef2f7;
  }
`;

export const StyledTBody = styled.tbody`
  overflow-y: auto;

  &.bulk {
    // Flex grow to fill available space, essential for scrolling within the body
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  &.detail {
    // Flex grow to fill available space, but allowed to shrink to fit viewport (critical for footer sticking)
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  > tr:nth-child(even) {
    background-color: var(--CLOUD, #fcfdfe);
    border-top: 1px solid var(--Gray-50, #e9edf5);
    border-bottom: 1.6px solid var(--Gray-50, #e9edf5);
  }
`;

export const StyledTFoot = styled.tfoot`
  position: sticky;
  bottom: 0;
  z-index: 2;
  background-color: ${DETAIL_TOTALS_FOOTER_BG};
  font-weight: 600;
  border-top: ${DETAIL_TOTALS_FOOTER_BORDER};
  box-shadow: ${DETAIL_TOTALS_FOOTER_SHADOW};

  &.detail {
    // Don't shrink, push to bottom when content is short, stick when content overflows
    flex-shrink: 0;
    position: relative;
  }
`;

const StyledTr = css`
  align-items: center;
`;

export const StyledTHeadTr = styled.tr`
  ${StyledTr}
  height: 34px;
  border-bottom: 1px solid var(--Gray-50, #e9edf5);
`;

export const StyledTBodyTr = styled.tr`
  ${StyledTr}
  height: 48px;
  /* 
   * Hardware Acceleration / Layer Promotion:
   * Forces the browser to promote each row to its own GPU layer.
   * This decouples the row's rendering from the container's paint cycle, 
   * eliminating visual tearing ("jerk") during horizontal scrolling of virtualized lists.
   */
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);

  &.is-even {
    background-color: var(--GHOST, #efeffd);
    border-top: 1px solid var(--Gray-50, #e9edf5);
    border-bottom: 1.6px solid var(--Gray-50, #e9edf5);
  }

  &.is-selected {
    background-color: var(--BLUE-LIGHT, #eef4ff);
  }
`;

export const StyledTFootTr = styled.tr`
  ${StyledTr}
  ${detailTotalsFooterRowCss}
  border-block: none;
`;

const StyledTCell = css`
  flex-shrink: 0 !important;
  overflow: hidden;
  padding: 0 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledTh = styled.th`
  ${StyledTCell}
  align-items: center;
  ${tableHeaderTextCss}
  display: flex;
  line-height: 34px;
  position: relative;

  &.justify-center {
    justify-content: center;

    > div {
      margin: 0 auto;
    }
  }

  &.justify-right {
    > div {
      margin-left: auto;
    }
  }

  &.bulk {
    flex-grow: 0 !important;

    &.center-align {
      text-align: center;
    }

    &.right-align {
      text-align: right;
    }
  }
`;

export const StyledThDiv = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
`;

export const StyledThResizer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  touch-action: none;
  background: transparent;
  z-index: 2;

  &::after {
    content: "";
    position: absolute;
    top: 25%;
    bottom: 25%;
    right: 4px; /* centres the bar in the 10px wrapper */
    width: 2px;
    background-color: #d9d9d9;
  }
`;

export const StyledColumnResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  touch-action: none;
  background: transparent;
  z-index: 3;

  &::after {
    content: "";
    position: absolute;
    top: 25%;
    bottom: 25%;
    right: 4px;
    width: 2px;
    background-color: #d9d9d9;
    transition: background-color 0.12s ease;
  }

  &:hover::after,
  &:focus-visible::after {
    background-color: #2060ea;
  }
`;

export const StyledColumnResizeOverlay = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999;

  .column-resize-readout {
    position: fixed;
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    color: #0e0e2c;
    background: #fff;
    border: 1px solid #ecf1f4;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(14, 14, 44, 0.12);
  }
`;

export const StyledColumnResizeGuide = styled.div`
  position: fixed;
  width: 2px;
  background-color: #2060ea;
  pointer-events: none;
  transform: translateX(-1px);
`;

export const StyledTd = styled.td`
  ${StyledTCell}
  font-weight: 400;
  white-space: nowrap;
  line-height: 48px;

  &.bulk {
    background: var(--Mist, #f7f8f9);
    flex-grow: 0 !important;
  }

  &.error {
    color: #cf324a;
    cursor: pointer;
  }

  &.center-align {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: normal;
    text-align: center;

    > span,
    > div {
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      line-height: normal;
    }

    input[type="checkbox"] {
      margin: 0;
      flex-shrink: 0;
    }
  }

  &.right-align {
    text-align: right;
    ${tableNumericCss}
    > span, > div {
      margin-left: auto;
    }
  }

  &.footer {
    background-color: ${DETAIL_TOTALS_FOOTER_BG};
    ${detailTotalsFooterValueCss}
    line-height: ${DETAIL_TOTALS_FOOTER_HEIGHT};
  }
`;

// Wrapper for the react-window List to handle strict overflow isolation
export const StyledVirtualListWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;
